/**
 * Blur, or something else, behind the person.
 *
 * MediaPipe's selfie segmentation answers, per frame, which pixels are a
 * person. Those pixels are kept and everything else is painted over — blurred
 * camera for `blur`, an image for `image` — onto a canvas whose
 * `captureStream(24)` is what gets published. The SFU never sees the camera
 * track at all, which is the point: a background that is applied after
 * publishing is a background the other end does not have.
 *
 * ── The one network fetch ───────────────────────────────────────────────────
 * Everything else this app loads is its own origin. Segmentation is the
 * exception: the WebAssembly runtime and the `.tflite` model are fetched from
 * a CDN the first time somebody turns a background on, about 3 MB, cached by
 * the browser afterwards. Nothing is fetched until then — a call with no
 * background costs nothing.
 *
 * The two constants below are the only cross-origin URLs in this app, and
 * `MEDIA_ORIGINS` in `shared/security-headers.ts` is what lets the browser
 * fetch them. Change either one and change that one too, or the background
 * silently stops applying: nothing throws, the menu still offers it and the
 * picture simply does not change.
 */

import type { Background } from './types';

export const MEDIAPIPE_WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';

export const SELFIE_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite';

/** The published frame rate, matching the camera's. */
export const BACKGROUND_FPS = 24;
export const BACKGROUND_BLUR_PX = 12;

interface Segmenter {
  segmentForVideo(
    frame: HTMLVideoElement,
    timestamp: number,
    callback: (result: { categoryMask?: { getAsUint8Array(): Uint8Array } | null }) => void
  ): void;
  close(): void;
}

/**
 * One camera track in, one canvas track out.
 *
 * Held open across a change of background — turning blur into an image swaps
 * what is painted, not the published track, so nothing renegotiates and the
 * far end sees no interruption.
 */
export class BackgroundProcessor {
  private segmenter: Segmenter | null = null;
  private video: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private context: CanvasRenderingContext2D | null = null;
  private maskCanvas: HTMLCanvasElement | null = null;
  private image: HTMLImageElement | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private background: Background = { kind: 'none' };
  private output: MediaStreamTrack | null = null;

  /** The track to publish. `none` hands the camera straight back, so the
   *  cost of segmentation is only paid by somebody who asked for it. */
  async apply(source: MediaStreamTrack, background: Background): Promise<MediaStreamTrack> {
    if (background.kind === 'none') {
      this.stop();
      return source;
    }

    this.background = background;
    await this.loadImage(background);

    if (this.output && this.video?.srcObject instanceof MediaStream) {
      const current = this.video.srcObject.getVideoTracks()[0];
      if (current === source) return this.output;
    }

    this.stop();

    const settings = source.getSettings();
    const width = settings.width ?? 640;
    const height = settings.height ?? 360;

    const video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([source]);
    await video.play().catch(() => {});
    this.video = video;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    this.canvas = canvas;
    this.context = canvas.getContext('2d');

    const mask = document.createElement('canvas');
    mask.width = width;
    mask.height = height;
    this.maskCanvas = mask;

    this.segmenter = await this.createSegmenter();
    this.timer = setInterval(() => this.frame(), Math.round(1000 / BACKGROUND_FPS));

    const track = canvas.captureStream(BACKGROUND_FPS).getVideoTracks()[0];
    if (!track) throw new Error('The background could not be applied.');
    this.output = track;
    return track;
  }

  stop(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.segmenter?.close();
    this.segmenter = null;
    this.output?.stop();
    this.output = null;
    if (this.video) {
      this.video.srcObject = null;
      this.video = null;
    }
    this.canvas = null;
    this.context = null;
    this.maskCanvas = null;
  }

  private async createSegmenter(): Promise<Segmenter> {
    const { FilesetResolver, ImageSegmenter } = await import('@mediapipe/tasks-vision');
    const fileset = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_BASE);
    const segmenter = await ImageSegmenter.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: SELFIE_MODEL_URL, delegate: 'GPU' },
      runningMode: 'VIDEO',
      outputCategoryMask: true,
      outputConfidenceMasks: false
    });
    return segmenter as unknown as Segmenter;
  }

  private async loadImage(background: Background): Promise<void> {
    if (background.kind !== 'image' || !background.url) {
      this.image = null;
      return;
    }
    if (this.image?.src === background.url) return;
    const image = new Image();
    image.src = background.url;
    await image.decode().catch(() => {});
    this.image = image;
  }

  private frame(): void {
    const { video, canvas, context, segmenter, maskCanvas } = this;
    if (!video || !canvas || !context || !segmenter || !maskCanvas) return;
    if (video.readyState < 2) return;

    segmenter.segmentForVideo(video, performance.now(), (result) => {
      const data = result.categoryMask?.getAsUint8Array();
      if (!data) return;
      this.draw(data);
    });
  }

  /**
   * The person, then everything behind them.
   *
   * `destination-in` keeps only what the mask covers; `destination-over` then
   * fills what is left. Doing it the other way round — background first, person
   * composited on top — needs the person cut out *before* it is drawn, which is
   * the same two operations with an extra canvas.
   *
   * The mask is one byte per pixel, non-zero where the model found a person.
   */
  private draw(mask: Uint8Array): void {
    const { canvas, context, maskCanvas, video, image } = this;
    if (!canvas || !context || !maskCanvas || !video) return;
    const { width, height } = canvas;

    const maskContext = maskCanvas.getContext('2d');
    if (!maskContext) return;
    const alpha = maskContext.createImageData(width, height);
    for (let i = 0; i < mask.length; i += 1) {
      alpha.data[i * 4 + 3] = mask[i] ? 255 : 0;
    }
    maskContext.putImageData(alpha, 0, 0);

    context.save();
    context.globalCompositeOperation = 'source-over';
    context.filter = 'none';
    context.clearRect(0, 0, width, height);
    context.drawImage(video, 0, 0, width, height);

    context.globalCompositeOperation = 'destination-in';
    context.drawImage(maskCanvas, 0, 0, width, height);

    context.globalCompositeOperation = 'destination-over';
    if (this.background.kind === 'image' && image) {
      context.drawImage(image, 0, 0, width, height);
    } else {
      context.filter = `blur(${BACKGROUND_BLUR_PX}px)`;
      context.drawImage(video, 0, 0, width, height);
    }
    context.restore();
  }
}
